import { Injectable } from '@angular/core';

import { mergeMap } from 'rxjs/operators';
import { combineLatest, iif, map, Observable, of } from 'rxjs';

import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { ILoanApplication } from 'src/app/core/services/loan-application/loan-application.model';

import { MobileApiService } from 'src/app/core/services/mobile-api';

import { AutoPayBankAccountVerificationService } from 'src/app/core/services/auto-pay-bank-account-verification/auto-pay-bank-account-verification.service';
import {
	AchBankAccountsService,
	IAchBankAccount
} from 'src/app/core/services/ach-bank-accounts/ach-bank-accounts.service';
import { DisbursementUtils } from 'src/app/core/services/loan-application/disbursement/disbursement-utils';
import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import { ProductSubStatusesEnum } from 'src/app/core/services/loan-application/product-offer/product/product.model';

@Injectable({
	providedIn: 'root'
})
export class ConfirmTermsService {
	constructor(
		private loanAppService: LoanApplicationService,
		private mobileApiService: MobileApiService,
		private autoPayBankAccountVerificationService: AutoPayBankAccountVerificationService,
		private bankAccountService: AchBankAccountsService,
		private routingService: RoutingService
	) {}

	loanApp: ILoanApplication = null;
	disbursement: DisbursementUtils;

	getNextRoute(): Observable<RoutingPathsEnum> {
		return this.loanAppService.updateLoanApplication().pipe(
			mergeMap((loanApp: ILoanApplication) => {
				this.loanApp = loanApp;
				this.disbursement = DisbursementUtils.fromLoanApp(loanApp);

				return combineLatest([
					this.getAutoPayEligible(loanApp),
					this.getBankValid(loanApp),
					this.mobileApiService.getRachEnrollmentEligibility(loanApp.id)
				]);
			}),
			map(([autoPayEligible, isBankValid, rachEnrollmentEligible]) => {
				if (
					(this.disbursement.hasBankAccountAndAutoPayNotComplete() && isBankValid) ||
					this.loanAppService.isRachWithoutAchOptInEligible() ||
					autoPayEligible ||
					(rachEnrollmentEligible && (this.disbursement.isOnlineCheck() || this.disbursement.isDebitCard()))
				) {
					return RoutingPathsEnum.esignAutoPay;
				} else if (
					this.disbursement.onlineNotificationEnabled &&
					this.disbursement.isChannelAnyStore() &&
					!this.routingService.isSplStorelessEligibleWithStatus(ProductSubStatusesEnum.complete)
				) {
					return RoutingPathsEnum.termsConfirmed;
				} else {
					return RoutingPathsEnum.esignSignDocument;
				}
			})
		);
	}

	getAutoPayEligible(loanApp: ILoanApplication): Observable<boolean> {
		return this.bankAccountService.refreshBankAccounts(loanApp.id).pipe(
			mergeMap((bankAccounts: IAchBankAccount[]) => {
				const bankAccountIds: [] = bankAccounts.map((account) => account.id) as [];
				if (bankAccountIds?.length > 0 && (this.disbursement.isTypeCheck() || this.disbursement.isDebitCard())) {
					return this.autoPayBankAccountVerificationService
						.verifyBankAccounts(bankAccountIds, this.loanAppService.loanApplicationId)
						.pipe(map((accounts: IAchBankAccount[]) => accounts.some((account) => account.status === 'ACTIVE')));
				}
				return of(false);
			})
		);
	}

	getBankValid(loanApp: ILoanApplication): Observable<boolean> {
		return this.mobileApiService.getAchBankAccount(loanApp.id).pipe(
			mergeMap((bankAccount) => {
				return iif(
					() => Boolean(bankAccount?.id),
					this.mobileApiService
						.getVerifyBankAccount(bankAccount.id, this.loanAppService.loanApplicationId)
						.pipe(map((bankAccount) => bankAccount?.status === 'ACTIVE')),
					of(false)
				);
			})
		);
	}
}
